import escapeRegExp from 'lodash/escapeRegExp';
import { useEffect, useRef, useState } from 'react';
import type { SlotRecord } from '../components/PlaceholderAutocomplete';
import { PlaceholderInputType, PromptOption } from '../samplePrompts';

// The label stands in the text as a whole name: a letter or digit typed against it ("Oy" → "Oyj") or a capitalised word
// after it ("Erwin" → "Erwin Parker") makes another name. No prompt's own text after a record slot starts capitalised.
const namesLabel = (text: string, label: string) =>
    new RegExp(`(?:^|[^\\p{L}\\p{N}])${escapeRegExp(label)}(?![\\p{L}\\p{N}]| +\\p{Lu})`, 'u').test(text);

export interface PlaceholderSession {
    baseText: string;
    placeholders: string[];
    /** The slot being filled; the slot count once every slot is filled. */
    currentIndex: number;
    values: string[];
    optionsMap?: Record<string, PromptOption[]>;
    defaults?: Record<string, string>;
    placeholderInputTypes?: Record<string, PlaceholderInputType>;
    noteId?: string;
    /** The record picked into each slot that takes one, by slot index. */
    records: (SlotRecord | undefined)[];
}

/**
 * How a ready-made prompt reaches the composer: its text, the options and inputs of its slots, and its id, which a
 * new chat's first message carries.
 */
export type PromptSelectHandler = (
    text: string,
    optionsMap?: Record<string, PromptOption[]>,
    config?: PlaceholderFillConfig,
    promptId?: string
) => void;

function extractPlaceholders(text: string): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const m of text.matchAll(/\[([^\]]+)\]/g)) {
        if (!seen.has(m[1])) {
            seen.add(m[1]);
            result.push(m[1]);
        }
    }
    return result;
}

function applyValues(baseText: string, placeholders: string[], values: string[]): string {
    let result = baseText;
    placeholders.forEach((ph, i) => {
        const val = values[i];
        result = result.split(`[${ph}]`).join(val !== '' ? val : `[${ph}]`);
    });
    return result;
}

interface UsePlaceholderFillParams {
    setValue: (name: 'message', value: string) => void;
    setFocus: (name: 'message') => void;
}

interface UsePlaceholderFillReturn {
    placeholderSession: PlaceholderSession | null;
    /** The composer: a press outside it ends the fill. */
    composerRef: React.RefObject<HTMLDivElement | null>;
    handlePromptSelect: (
        text: string,
        optionsMap?: Record<string, PromptOption[]>,
        config?: PlaceholderFillConfig
    ) => void;
    handlePlaceholderChange: (value: string) => void;
    handlePlaceholderNext: () => void;
    handlePlaceholderOptionSelect: (value: string) => void;
    handlePlaceholderRecordSelect: (record: SlotRecord) => void;
    handlePlaceholderDismiss: () => void;
    clearSession: () => void;
    /** The records picked into the prompt's slots whose label `text` still holds. */
    getPickedRecords: (text: string) => SlotRecord[];
    getPlaceholderFilledText: (currentText?: string) => string | null;
    findUnfilledPlaceholder: (text: string) => string | undefined;
}

export interface PlaceholderFillConfig {
    defaults?: Record<string, string>;
    placeholderInputTypes?: Record<string, PlaceholderInputType>;
    noteId?: string;
}

export function usePlaceholderFill({ setValue, setFocus }: UsePlaceholderFillParams): UsePlaceholderFillReturn {
    const [placeholderSession, setPlaceholderSession] = useState<PlaceholderSession | null>(null);
    // The last selected prompt's placeholders. Outlives a dismissed session, which leaves the literal
    // "[…]" in the composer; `clearSession` — called once the message is sent or the field emptied — forgets them.
    const [promptPlaceholders, setPromptPlaceholders] = useState<string[]>([]);
    // The records picked into its slots, by slot index, kept as long as its placeholders.
    const [pickedRecords, setPickedRecords] = useState<(SlotRecord | undefined)[]>([]);
    const composerRef = useRef<HTMLDivElement>(null);

    const handlePromptSelect = (
        text: string,
        optionsMap?: Record<string, PromptOption[]>,
        config?: PlaceholderFillConfig
    ) => {
        const placeholders = extractPlaceholders(text);
        setPromptPlaceholders(placeholders);
        setPickedRecords([]);
        if (placeholders.length === 0) {
            setPlaceholderSession(null);
            setValue('message', text);
            setTimeout(() => setFocus('message'), 0);
            return;
        }
        const values = placeholders.map((placeholder) => config?.defaults?.[placeholder] ?? '');
        setValue('message', applyValues(text, placeholders, values));
        setPlaceholderSession({
            baseText: text,
            placeholders,
            currentIndex: 0,
            values,
            optionsMap,
            defaults: config?.defaults,
            placeholderInputTypes: config?.placeholderInputTypes,
            noteId: config?.noteId,
            records: [],
        });
    };

    const handlePlaceholderChange = (value: string) => {
        if (!placeholderSession) return;
        const newValues = [...placeholderSession.values];
        newValues[placeholderSession.currentIndex] = value;
        setPlaceholderSession((prev) => (prev ? { ...prev, values: newValues } : prev));
        setValue('message', applyValues(placeholderSession.baseText, placeholderSession.placeholders, newValues));
    };

    const advance = (newValues: string[], records = placeholderSession?.records ?? []) => {
        if (!placeholderSession) return;
        const { currentIndex, placeholders, baseText } = placeholderSession;
        setValue('message', applyValues(baseText, placeholders, newValues));
        const nextIndex = currentIndex + 1;
        if (nextIndex < placeholders.length || newValues.every((value) => value !== '')) {
            // Past the last slot with every slot filled (`currentIndex` = the slot count), the session stays, so the
            // composer shows the prompt ready to send with its picked records as chips.
            setPlaceholderSession((prev) =>
                prev ? { ...prev, currentIndex: nextIndex, values: newValues, records } : prev
            );
        } else {
            // A slot was skipped: the prompt goes back to the field as text, its empty slots still `[…]`.
            setPlaceholderSession(null);
        }
        if (nextIndex >= placeholders.length) {
            setTimeout(() => setFocus('message'), 0);
        }
    };

    const handlePlaceholderNext = () => {
        if (!placeholderSession) return;
        advance(placeholderSession.values);
    };

    const handlePlaceholderOptionSelect = (value: string) => {
        if (!placeholderSession) return;
        const newValues = [...placeholderSession.values];
        newValues[placeholderSession.currentIndex] = value;
        advance(newValues);
    };

    const handlePlaceholderRecordSelect = (record: SlotRecord) => {
        if (!placeholderSession) return;
        const newValues = [...placeholderSession.values];
        newValues[placeholderSession.currentIndex] = record.label;
        const records = [...placeholderSession.records];
        records[placeholderSession.currentIndex] = record;
        setPickedRecords(records);
        advance(newValues, records);
    };

    const handlePlaceholderDismiss = () => {
        setPlaceholderSession(null);
        setTimeout(() => setFocus('message'), 0);
    };

    const clearSession = () => {
        setPlaceholderSession(null);
        setPromptPlaceholders([]);
        setPickedRecords([]);
    };

    const getPickedRecords = (text: string) =>
        pickedRecords.filter((record): record is SlotRecord => record != null && namesLabel(text, record.label));

    const getPlaceholderFilledText = (currentText?: string) => {
        if (!placeholderSession) return null;
        const filledText = applyValues(
            placeholderSession.baseText,
            placeholderSession.placeholders,
            placeholderSession.values
        );
        if (currentText !== undefined && currentText !== placeholderSession.baseText && currentText !== filledText) {
            return null;
        }
        return filledText;
    };

    /**
     * The first of the selected prompt's placeholders still in `text` as a literal "[…]", or undefined.
     * Senders refuse such a message: the assistant would get the template instead of a question.
     * Only the prompt's own placeholders count, and only until `clearSession`, so brackets the user
     * types in a later message never block it.
     */
    const findUnfilledPlaceholder = (text: string) =>
        promptPlaceholders.map((placeholder) => `[${placeholder}]`).find((literal) => text.includes(literal));

    // TODO: cascading-set-state skip — DOM listener registration; the inner handler dismisses session and refocuses textarea, which is event-driven, not derivable.
    useEffect(() => {
        if (!placeholderSession) return;

        const handleMouseDown = (e: MouseEvent) => {
            // A slot's menu or sheet is portalled out of the composer; picking from it is not leaving the fill.
            if (
                e.target instanceof Element &&
                e.target.closest(
                    '[data-slot="combobox-content"], [data-slot="dropdown-menu-content"], [data-slot^="drawer-"]'
                )
            ) {
                return;
            }
            const composer = composerRef.current;
            if (composer && !composer.contains(e.target as Node)) {
                setPlaceholderSession(null);
                setTimeout(() => setFocus('message'), 0);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [placeholderSession, setFocus]);

    return {
        placeholderSession,
        composerRef,
        handlePromptSelect,
        handlePlaceholderChange,
        handlePlaceholderNext,
        handlePlaceholderOptionSelect,
        handlePlaceholderRecordSelect,
        handlePlaceholderDismiss,
        clearSession,
        getPickedRecords,
        getPlaceholderFilledText,
        findUnfilledPlaceholder,
    };
}
